import React, { useState, useEffect, useRef, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExtractionDetail from './ExtractionDetail';
import useAdPipeline from './useAdPipeline';
import generateAdPdf from './generateAdPdf';
import { clearCachedKeyframes } from './keyframeCache';
import TagEditor from 'shared/components/TagEditor';
import { MODEL_OPTIONS, DEFAULT_ANALYSIS_MODEL, DEFAULT_EXTRACTION_MODEL } from './models';
import FallbackVideo from './FallbackVideo';
import AdLibraryService from 'services/AdLibraryService';

export const AD_DETAIL_TAB_KEYS = ['transcript', 'analysis', 'extraction', 'pipeline'] as const;
export type AdDetailTabKey = typeof AD_DETAIL_TAB_KEYS[number];

const TAB_KEYS = AD_DETAIL_TAB_KEYS;
type TabKey = AdDetailTabKey;

interface Props {
  adId: string | null;
  initialTab?: TabKey;
  onTabChange?: (tab: TabKey) => void;
  onClose: () => void;
  onRefresh?: () => void;
  onAdUpdated?: (ad: import('./types').ScrapedAd) => void;
}

export default function AdDetailDialog({ adId, initialTab, onTabChange, onClose, onRefresh, onAdUpdated }: Props) {
  const {
    ad, transcript, transcriptSegments, analyses, prompts, ciDetail,
    loading, ciLoading, analyzing, extracting, pipelineRunning,
    error, clearError,
    deletingAnalysisId,
    loadCiDetail, transcribe, analyze, deleteAnalysis, extract, runPipeline,
  } = useAdPipeline(adId, onRefresh, onAdUpdated);

  // UI-only state
  const initialTabIndex = initialTab ? TAB_KEYS.indexOf(initialTab) : 0;
  const [tab, setTabState] = useState(initialTabIndex >= 0 ? initialTabIndex : 0);
  const setTab = (index: number) => {
    setTabState(index);
    onTabChange?.(TAB_KEYS[index]);
  };
  const [selectedPromptIds, setSelectedPromptIds] = useState<string[]>([]);
  const [selectedAnalysisIds, setSelectedAnalysisIds] = useState<string[]>([]);
  const [analysisModel, setAnalysisModel] = useState(DEFAULT_ANALYSIS_MODEL);
  const [extractionModel, setExtractionModel] = useState(DEFAULT_EXTRACTION_MODEL);
  const [expandAnalyze, setExpandAnalyze] = useState(true);
  const [expandExtract, setExpandExtract] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  // ── Tags ──
  const tagService = useMemo(() => new AdLibraryService(), []);
  const [tags, setTags] = useState<string[]>([]);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  useEffect(() => { setTags(ad?.tags ?? []); }, [ad?.tags]);
  useEffect(() => {
    if (!adId) return;
    let cancelled = false;
    tagService.listAdTags().then(res => { if (!cancelled && res) setTagSuggestions(res.tags); });
    return () => { cancelled = true; };
  }, [adId, tagService]);
  const applyTags = (next: string[]) => {
    setTags(next);
    if (ad) onAdUpdated?.({ ...ad, tags: next });
  };
  const handleAddTag = async (tag: string) => {
    if (!adId) return;
    const res = await tagService.addAdTag(adId, tag);
    if (res) { applyTags(res.tags); setTagSuggestions(prev => Array.from(new Set([...prev, ...res.tags])).sort()); }
  };
  const handleRemoveTag = async (tag: string) => {
    if (!adId) return;
    const res = await tagService.removeAdTag(adId, tag);
    if (res) applyTags(res.tags);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setToast(`Copied ${label}: ${text}`);
  };

  // Reset tab and expand state when ad changes
  useEffect(() => {
    if (adId) {
      const idx = initialTab ? TAB_KEYS.indexOf(initialTab) : 0;
      setTabState(idx >= 0 ? idx : 0);
      setExpandAnalyze(true);
      setExpandExtract(true);
    }
  }, [adId]);

  // Lazy-load CI detail when Transcript / Extraction / Pipeline tab is
  // selected. Transcript needs it for the keyframes header at the top.
  useEffect(() => {
    if (tab === 0 || tab === 2 || tab === 3) loadCiDetail();
  }, [tab, loadCiDetail]);

  // Per-line timestamps: align each Haiku-reformatted line back to the
  // Whisper segment whose text it shares its first words with, walking
  // forward with a cursor so order is preserved. Approximate to Whisper-
  // segment granularity (a few seconds) — accurate enough to pair lines
  // with keyframe shots.
  const lineTimestamps = useMemo<(number | null)[]>(() => {
    if (!transcript) return [];
    const lines = transcript.split(/\r?\n/);
    if (!transcriptSegments || transcriptSegments.length === 0) {
      return lines.map(() => null);
    }
    const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
    const segs = transcriptSegments.map(s => ({ start: s.start, text: normalize(s.text) }));
    let cursor = 0;
    return lines.map(line => {
      const probe = normalize(line).split(' ').filter(Boolean).slice(0, 5).join(' ');
      if (!probe) return null;
      const needle = probe.slice(0, Math.min(probe.length, 20));
      for (let i = cursor; i < segs.length; i++) {
        if (segs[i].text.includes(needle)) {
          cursor = i;
          return segs[i].start;
        }
      }
      return null;
    });
  }, [transcript, transcriptSegments]);

  const formatTimestamp = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s - m * 60;
    return `${m}:${sec.toFixed(1).padStart(4, '0')}`;
  };

  // Sync selectedAnalysisIds when analyses change (auto-select DONE analyses)
  useEffect(() => {
    setSelectedAnalysisIds(analyses.filter(a => a.status === 'DONE').map(a => a.analysisId));
  }, [analyses]);

  // Sync selectedPromptIds when prompts or analyses change (auto-deselect already-done prompts)
  useEffect(() => {
    const donePromptIds = new Set(analyses.filter(a => a.status === 'DONE').map(a => a.promptId));
    setSelectedPromptIds(prompts.filter(p => !donePromptIds.has(p.promptId)).map(p => p.promptId));
  }, [prompts, analyses]);

  // Auto-collapse completed steps
  const anyAnalysisDone = analyses.some(a => a.status === 'DONE');
  const anyExtractionDone = ciDetail?.features != null;
  useEffect(() => { if (anyAnalysisDone) setExpandAnalyze(false); }, [anyAnalysisDone]);
  useEffect(() => { if (anyExtractionDone) setExpandExtract(false); }, [anyExtractionDone]);

  // Per-analysis collapse state. Analyses from retired prompts (the deleted
  // "LLM JSON Analysis", or legacy names) start collapsed; the current
  // single-prompt output is the headline read and stays expanded. We only
  // seed the default once per analysis id — if the user toggles, their
  // choice sticks even on later analyses-list refreshes.
  const [collapsedAnalysisIds, setCollapsedAnalysisIds] = useState<Set<string>>(new Set());
  const seededAnalysisIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (analyses.length === 0 || prompts.length === 0) return;
    const additions: string[] = [];
    for (const a of analyses) {
      if (seededAnalysisIdsRef.current.has(a.analysisId)) continue;
      seededAnalysisIdsRef.current.add(a.analysisId);
      const prompt = prompts.find(p => p.promptId === a.promptId);
      const name = (prompt?.name || '').toLowerCase();
      // No prompt row = the prompt was retired (e.g. "LLM JSON Analysis").
      if (!prompt || name.includes('llm json')) {
        additions.push(a.analysisId);
      }
    }
    if (additions.length === 0) return;
    setCollapsedAnalysisIds(prev => {
      const next = new Set(prev);
      for (const id of additions) next.add(id);
      return next;
    });
  }, [analyses, prompts]);
  const toggleCollapsedAnalysis = (analysisId: string) => {
    setCollapsedAnalysisIds(prev => {
      const next = new Set(prev);
      if (next.has(analysisId)) next.delete(analysisId); else next.add(analysisId);
      return next;
    });
  };

  // Confirm dialog before deleting an analysis.
  const [confirmDeleteAnalysisId, setConfirmDeleteAnalysisId] = useState<string | null>(null);
  const confirmDeleteAnalysis = async () => {
    if (!confirmDeleteAnalysisId) return;
    const id = confirmDeleteAnalysisId;
    setConfirmDeleteAnalysisId(null);
    await deleteAnalysis(id);
  };

  if (!adId) return null;

  const transcriptionDone = ad?.transcriptionStatus === 'DONE' || !!transcript;
  const transcriptionInProgress = ad?.transcriptionStatus === 'QUEUED' || ad?.transcriptionStatus === 'PROCESSING';
  const analysisDone = analyses.some(a => a.status === 'DONE');
  const donePromptIds = new Set(analyses.filter(a => a.status === 'DONE').map(a => a.promptId));
  const extractionComplete = ciDetail?.features != null;
  const extractionFailed = ad?.extractionStatus === 'FAILED';

  const handleAnalyze = () => analyze(selectedPromptIds, analysisModel);
  const handleExtract = () => extract(selectedAnalysisIds, extractionModel);
  const handleRunPipeline = () => runPipeline(selectedPromptIds, analysisModel, extractionModel);
  const handleDownloadPdf = async () => {
    if (!ad) return;
    setPdfGenerating(true);
    try {
      await generateAdPdf(ad, transcript, ciDetail);
    } catch (err) {
      setToast('Failed to generate PDF');
    }
    setPdfGenerating(false);
  };

  return (
    <Dialog open={!!adId} onClose={onClose} maxWidth={false}
      PaperProps={{ sx: {
        // 70% of viewport, but never smaller than the previous lg-fullWidth
        // baseline (~1200px) when the screen has the room for it; clamp at
        // 95vw / 95vh on smaller viewports so it always fits.
        width: 'min(95vw, max(70vw, 1200px))',
        height: 'min(95vh, max(70vh, 600px))',
        maxWidth: 'none',
        maxHeight: '95vh',
      } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          Ad Detail
          {ad && (
            <>
              <Typography component="span" color="text.secondary" sx={{ mx: 0.5 }}>—</Typography>
              <Typography component="span" fontWeight={600}
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                onClick={() => copyToClipboard(ad.pageName || '', 'page name')}>
                {ad.pageName}
              </Typography>
              <Typography component="span" color="text.secondary" sx={{ mx: 0.5 }}>—</Typography>
              <Typography component="span" variant="body2" color="text.secondary"
                sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.75rem', '&:hover': { textDecoration: 'underline' } }}
                onClick={() => copyToClipboard(adId || '', 'ad ID')}>
                {adId?.slice(0, 8)}
              </Typography>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Download PDF">
            <IconButton onClick={handleDownloadPdf} disabled={pdfGenerating || !ad}>
              {pdfGenerating ? <CircularProgress size={20} /> : <DownloadIcon />}
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : ad ? (
          <Box sx={{ display: 'flex', gap: 3, minHeight: 400 }}>
            {/* Left: Video + Meta */}
            <Box sx={{ width: '40%', flexShrink: 0 }}>
              {(ad.videoUrl || ciDetail?.archivedVideoShareUrl) ? (
                <FallbackVideo primary={ad.videoUrl} fallback={ciDetail?.archivedVideoShareUrl} controls style={{ width: '100%', borderRadius: 8, backgroundColor: '#000' }} />
              ) : ad.videoThumbnailUrl ? (
                <img src={ad.videoThumbnailUrl} alt="thumbnail" style={{ width: '100%', borderRadius: 8 }} />
              ) : (
                <Paper sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                  <Typography color="text.secondary">No video</Typography>
                </Paper>
              )}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>{ad.pageName}</Typography>
                {ad.headline && <Typography variant="body2" sx={{ mt: 0.5 }}>{ad.headline}</Typography>}
                {ad.caption && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{ad.caption}</Typography>}
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {ad.cta && <Chip label={`CTA: ${ad.cta}`} size="small" />}
                  {ad.platforms && <Chip label={ad.platforms} size="small" variant="outlined" />}
                  {ad.startDate && <Chip label={`Started: ${ad.startDate}`} size="small" variant="outlined" />}
                  {ad.impressionRank && <Chip label={`Rank #${ad.impressionRank}`} size="small" variant="outlined" />}
                </Box>
                {(ciDetail?.rankHistory?.length ?? 0) > 1 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    Rank over time:{' '}
                    {(ciDetail?.rankHistory ?? []).slice(-10).map((obs, i, arr) => (
                      <span key={i}>
                        #{obs.impressionRank ?? '?'}
                        {obs.observedAt && ` (${new Date(obs.observedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})`}
                        {i < arr.length - 1 && ' → '}
                      </span>
                    ))}
                  </Typography>
                )}
                {ad.landingPageUrl && (
                  <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-all' }}>
                    <a href={ad.landingPageUrl} target="_blank" rel="noopener noreferrer">{ad.landingPageUrl}</a>
                  </Typography>
                )}
                {ciDetail?.archivedVideoShareUrl && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>
                      <strong>Shared video link</strong> (expires in 30 days):{' '}
                      <a href={ciDetail.archivedVideoShareUrl} target="_blank" rel="noopener noreferrer">{ciDetail.archivedVideoShareUrl}</a>
                    </Typography>
                    <Tooltip title="Copy share link">
                      <IconButton size="small" onClick={() => copyToClipboard(ciDetail.archivedVideoShareUrl!, 'share link')}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                <Divider sx={{ my: 1.5 }} />
                <TagEditor
                  tags={tags}
                  suggestions={tagSuggestions}
                  onAdd={handleAddTag}
                  onRemove={handleRemoveTag}
                />
              </Box>
            </Box>

            {/* Right: Tabs */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
                <Tab label="Transcript" />
                <Tab label={`Analysis (${analyses.length})`} />
                <Tab label="Extraction" />
                <Tab label="Pipeline" />
              </Tabs>

              {/* ── Transcript Tab ── */}
              {tab === 0 && (
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  {/* Keyframes captured during video shot detection. Shown at
                      the top of the transcript so they can be matched against
                      the per-line timestamps below. */}
                  {ciDetail?.keyframes && ciDetail.keyframes.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Keyframes ({ciDetail.keyframes.length})
                        </Typography>
                        <Tooltip title="Refresh keyframes (also clears the local + server cache)" arrow>
                          <span>
                            <IconButton size="small" disabled={ciLoading}
                              onClick={() => {
                                if (adId) clearCachedKeyframes(adId);
                                loadCiDetail({ refresh: true });
                              }}>
                              {ciLoading ? <CircularProgress size={14} /> : <RefreshIcon fontSize="inherit" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                        {ciDetail.keyframes.map(kf => (
                          <Box key={kf.shotOrder} sx={{ width: 140, flexShrink: 0 }}>
                            <img
                              src={kf.url}
                              alt={`Keyframe ${kf.shotOrder}`}
                              loading="lazy"
                              style={{ width: '100%', borderRadius: 4, display: 'block', backgroundColor: '#000' }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', fontFamily: 'monospace' }}>
                              #{kf.shotOrder} · {kf.startTime.toFixed(1)}s–{kf.endTime.toFixed(1)}s
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {transcript ? (
                    <Paper variant="outlined" sx={{ p: 0, fontSize: 13, maxHeight: 500, overflow: 'auto' }}>
                      {/* Line-numbered gutter. The transcription worker writes
                          one Whisper segment per line — so the line numbers
                          shown here line up 1:1 with the lineStart/lineEnd
                          stored on persuasion beats and ad segments. Per-line
                          timestamps on the right are derived by aligning each
                          line back to Whisper's verbose JSON (approximate). */}
                      {(() => {
                        const lines = transcript.split(/\r?\n/);
                        return lines.map((line, i) => {
                          const lineNum = i + 1;
                          const ts = lineTimestamps[i];
                          return (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', borderBottom: i === lines.length - 1 ? 'none' : '1px solid', borderColor: 'divider' }}>
                              <Box sx={{ width: 44, flexShrink: 0, py: 0.5, px: 1, textAlign: 'right', fontFamily: 'monospace', fontSize: 11, color: 'text.secondary', bgcolor: '#fafafa', userSelect: 'none' }}>
                                {lineNum}
                              </Box>
                              <Box sx={{ flex: 1, py: 0.5, px: 1.5, whiteSpace: 'pre-wrap' }}>
                                {line || <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>}
                              </Box>
                              <Box sx={{ width: 56, flexShrink: 0, py: 0.5, px: 1, textAlign: 'right', fontFamily: 'monospace', fontSize: 11, color: 'text.disabled', userSelect: 'none' }}>
                                {ts != null ? formatTimestamp(ts) : ''}
                              </Box>
                            </Box>
                          );
                        });
                      })()}
                    </Paper>
                  ) : transcriptionInProgress ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2">Transcription in progress...</Typography>
                    </Box>
                  ) : ad?.transcriptionStatus === 'FAILED' ? (
                    /* Bug 6: surface transcription failures */
                    <Box sx={{ py: 2 }}>
                      <Chip label="FAILED" size="small" color="error" sx={{ mb: 1 }} />
                      <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                        Transcription failed.
                      </Typography>
                      {ad.transcriptionError && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, whiteSpace: 'pre-wrap' }}>
                          {ad.transcriptionError}
                        </Typography>
                      )}
                      {ad.videoUrl && (
                        <Button variant="outlined" size="small" color="error" onClick={transcribe}>
                          Retry Transcription
                        </Button>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No transcript available</Typography>
                      {ad.videoUrl && (
                        <Button variant="outlined" size="small" onClick={transcribe}>Transcribe Now</Button>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* ── Analysis Tab ── */}
              {tab === 1 && (
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Select prompts to analyze with:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                      {prompts.map(p => (
                        <FormControlLabel key={p.promptId}
                          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {p.name}
                            {donePromptIds.has(p.promptId) && <Chip label="Done" size="small" color="success" sx={{ height: 18, fontSize: '0.6rem' }} />}
                          </Box>}
                          control={<Checkbox size="small" checked={selectedPromptIds.includes(p.promptId)}
                            onChange={(e) => setSelectedPromptIds(prev => e.target.checked ? [...prev, p.promptId] : prev.filter(id => id !== p.promptId))} />}
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Model</InputLabel>
                        <Select value={analysisModel} label="Model" onChange={(e) => setAnalysisModel(e.target.value)}>
                          {MODEL_OPTIONS.map((m) => (
                            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained" size="small"
                        onClick={handleAnalyze}
                        disabled={selectedPromptIds.length === 0 || analyzing || !ad.transcriptionJobId}
                      >
                        {analyzing ? <><CircularProgress size={14} sx={{ mr: 0.5 }} /> Analyzing...</> : `Analyze with ${selectedPromptIds.length} Prompt${selectedPromptIds.length !== 1 ? 's' : ''}`}
                      </Button>
                    </Box>
                  </Paper>
                  {analyses.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No analyses yet. Transcribe the ad first, then run analysis.</Typography>
                  ) : (() => {
                    // Detect duplicate prompt names to show timestamps
                    const promptNameCounts: Record<string, number> = {};
                    for (const a of analyses) {
                      const name = prompts.find(p => p.promptId === a.promptId)?.name || 'Unknown';
                      promptNameCounts[name] = (promptNameCounts[name] || 0) + 1;
                    }
                    return analyses.map((a) => {
                      const promptName = prompts.find(p => p.promptId === a.promptId)?.name || 'Unknown';
                      const showTimestamp = promptNameCounts[promptName] > 1;
                      const isCollapsed = collapsedAnalysisIds.has(a.analysisId);
                      return (
                        <Paper key={a.analysisId} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                              <IconButton size="small" onClick={() => toggleCollapsedAnalysis(a.analysisId)}
                                aria-label={isCollapsed ? 'Expand' : 'Collapse'}>
                                {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                              </IconButton>
                              <Chip label={a.status} size="small" color={a.status === 'DONE' ? 'success' : a.status === 'FAILED' ? 'error' : 'warning'} />
                              <Typography variant="body2" fontWeight={600}>
                                {promptName}
                                {showTimestamp && (
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                    ({new Date(a.createdAt + 'Z').toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })})
                                  </Typography>
                                )}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {a.model} | {a.tokensIn}in / {a.tokensOut}out
                              </Typography>
                              {deletingAnalysisId === a.analysisId ? (
                                <CircularProgress size={16} />
                              ) : (
                                <IconButton size="small" onClick={() => setConfirmDeleteAnalysisId(a.analysisId)}
                                  disabled={!!deletingAnalysisId}
                                  sx={{ opacity: 0.4, '&:hover': { opacity: 1, color: 'error.main' } }}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                          <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                            {a.resultText && (
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: 13, maxHeight: 200, overflow: 'auto' }}>
                                {a.resultText}
                              </Typography>
                            )}
                            {a.errorMessage && (
                              <Typography variant="body2" color="error">{a.errorMessage}</Typography>
                            )}
                          </Collapse>
                        </Paper>
                      );
                    });
                  })()}
                </Box>
              )}

              {/* ── Extraction Tab ── */}
              {tab === 2 && (
                <Box sx={{ flex: 1 }}>
                  {ciLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                  ) : ciDetail?.features ? (
                    <>
                      {/* Bug 8: show re-extraction failure even when old features exist */}
                      {extractionFailed && !extracting && (
                        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderColor: 'error.main' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label="FAILED" size="small" color="error" />
                            <Typography variant="body2" color="error">
                              Re-extraction failed. Showing previous results.
                            </Typography>
                          </Box>
                          {ad?.extractionError && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontFamily: 'monospace', bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                              {ad.extractionError}
                            </Typography>
                          )}
                        </Paper>
                      )}
                      {(() => {
                        const doneAnalyses = analyses.filter(a => a.status === 'DONE');
                        return (
                          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Analyses to include in extraction:</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                              {doneAnalyses.map(a => {
                                const promptName = prompts.find(p => p.promptId === a.promptId)?.name || 'Unknown';
                                return (
                                  <FormControlLabel key={a.analysisId} label={promptName}
                                    control={<Checkbox size="small" checked={selectedAnalysisIds.includes(a.analysisId)}
                                      onChange={(e) => setSelectedAnalysisIds(prev => e.target.checked ? [...prev, a.analysisId] : prev.filter(id => id !== a.analysisId))} />}
                                  />
                                );
                              })}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                              <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>Model</InputLabel>
                                <Select value={extractionModel} label="Model" onChange={(e) => setExtractionModel(e.target.value)}>
                                  {MODEL_OPTIONS.map((m) => (
                                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <Button variant="outlined" size="small" color="warning"
                                onClick={handleExtract}
                                disabled={extracting || selectedAnalysisIds.length === 0}>
                                {extracting ? <><CircularProgress size={14} sx={{ mr: 0.5 }} /> Re-extracting...</> : 'Re-run Extraction'}
                              </Button>
                            </Box>
                            {ciDetail.updatedAt && (
                              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block', textAlign: 'right', fontSize: '0.65rem' }}>
                                Last extracted: {new Date(ciDetail.updatedAt + 'Z').toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })} PT
                              </Typography>
                            )}
                          </Paper>
                        );
                      })()}
                      <Box sx={{ position: 'relative' }}>
                        {extracting && (
                          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(255,255,255,0.7)', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={24} />
                              <Typography variant="body2" color="text.secondary">Extracting...</Typography>
                            </Box>
                          </Box>
                        )}
                        <Box sx={extracting ? { opacity: 0.4, pointerEvents: 'none' } : {}}>
                          <ExtractionDetail detail={ciDetail} transcript={transcript} onCopy={copyToClipboard} />
                        </Box>
                      </Box>
                    </>
                  ) : extracting ? (
                    <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={32} />
                      <Typography variant="body2" color="text.secondary">Extraction in progress...</Typography>
                      <Typography variant="caption" color="text.secondary">This may take a few minutes. The page will update automatically.</Typography>
                    </Box>
                  ) : extractionFailed ? (
                    <Box sx={{ py: 2 }}>
                      <Paper variant="outlined" sx={{ p: 2, mb: 1.5, borderColor: 'error.main' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          {ad?.extractionError ? (
                            <Tooltip title={ad.extractionError} arrow>
                              <Chip label="FAILED" size="small" color="error" sx={{ cursor: 'default' }} />
                            </Tooltip>
                          ) : (
                            <Chip label="FAILED" size="small" color="error" />
                          )}
                          <Typography variant="body2" color="error">
                            Extraction failed. You can retry below.
                          </Typography>
                        </Box>
                        {ad?.extractionError && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontFamily: 'monospace', bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                            {ad.extractionError}
                          </Typography>
                        )}
                        {(() => {
                          const doneAnalyses = analyses.filter(a => a.status === 'DONE');
                          if (doneAnalyses.length === 0) {
                            return <Typography variant="body2" color="text.secondary">Run analysis first (go to the Analysis tab).</Typography>;
                          }
                          return (
                            <>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Analyses to include in extraction:</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                                {doneAnalyses.map(a => {
                                  const promptName = prompts.find(p => p.promptId === a.promptId)?.name || 'Unknown';
                                  return (
                                    <FormControlLabel key={a.analysisId} label={promptName}
                                      control={<Checkbox size="small" checked={selectedAnalysisIds.includes(a.analysisId)}
                                        onChange={(e) => setSelectedAnalysisIds(prev => e.target.checked ? [...prev, a.analysisId] : prev.filter(id => id !== a.analysisId))} />}
                                    />
                                  );
                                })}
                              </Box>
                              <Box sx={{ mt: 1 }}>
                                <Button variant="contained" size="small" color="error" onClick={handleExtract} disabled={selectedAnalysisIds.length === 0}>
                                  Retry Extraction
                                </Button>
                              </Box>
                            </>
                          );
                        })()}
                      </Paper>
                    </Box>
                  ) : (
                    <Box sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        No extraction data yet. Select which analyses to use for extraction:
                      </Typography>
                      {(() => {
                        const doneAnalyses = analyses.filter(a => a.status === 'DONE');
                        if (doneAnalyses.length === 0) {
                          return <Typography variant="body2" color="text.secondary">Run analysis first (go to the Analysis tab).</Typography>;
                        }
                        return (
                          <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Analyses to include in extraction:</Typography>
                            {doneAnalyses.map(a => {
                              const promptName = prompts.find(p => p.promptId === a.promptId)?.name || 'Unknown';
                              return (
                                <FormControlLabel key={a.analysisId} label={promptName}
                                  control={<Checkbox size="small" checked={selectedAnalysisIds.includes(a.analysisId)}
                                    onChange={(e) => setSelectedAnalysisIds(prev => e.target.checked ? [...prev, a.analysisId] : prev.filter(id => id !== a.analysisId))} />}
                                />
                              );
                            })}
                            <Box sx={{ mt: 1 }}>
                              <Button variant="contained" size="small" onClick={handleExtract} disabled={selectedAnalysisIds.length === 0}>
                                {`Run Extraction with ${selectedAnalysisIds.length} Analysis${selectedAnalysisIds.length !== 1 ? 'es' : ''}`}
                              </Button>
                            </Box>
                          </Paper>
                        );
                      })()}
                    </Box>
                  )}
                </Box>
              )}

              {/* ── Pipeline Tab ── */}
              {tab === 3 && (
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  {ciLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                  ) : <>
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      variant="contained" size="small"
                      onClick={handleRunPipeline}
                      disabled={pipelineRunning || transcriptionInProgress || analyzing || extracting}
                    >
                      {pipelineRunning ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                      {pipelineRunning ? 'Pipeline Running...' : 'Run Pipeline'}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Runs from the earliest incomplete step through extraction.
                    </Typography>
                  </Box>
                  <Stepper orientation="vertical">
                    {/* Fetched */}
                    <Step active completed>
                      <StepLabel StepIconComponent={() => <CheckCircleIcon color="success" fontSize="small" />}>
                        Fetched
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {ad.scrapedAt ? new Date(ad.scrapedAt).toLocaleDateString() : ''}
                        </Typography>
                      </StepLabel>
                    </Step>

                    {/* Transcribed */}
                    <Step active completed={transcriptionDone}>
                      <StepLabel
                        StepIconComponent={() =>
                          transcriptionDone ? <CheckCircleIcon color="success" fontSize="small" /> :
                          transcriptionInProgress ? <HourglassEmptyIcon color="warning" fontSize="small" /> :
                          <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                        }
                      >
                        Transcribed
                        <Chip label={transcriptionDone ? 'Done' : transcriptionInProgress ? ad.transcriptionStatus : 'Not started'} size="small" sx={{ ml: 1 }}
                          color={transcriptionDone ? 'success' : transcriptionInProgress ? 'warning' : 'default'} />
                      </StepLabel>
                      {!transcriptionDone && !transcriptionInProgress && (
                        <Box sx={{ pl: 4, pb: 1, pt: 0.5 }}>
                          <Button variant="outlined" size="small" onClick={transcribe} disabled={!ad.videoUrl}>
                            Transcribe
                          </Button>
                        </Box>
                      )}
                    </Step>

                    {/* Analyzed */}
                    <Step active completed={analysisDone}>
                      <StepLabel
                        StepIconComponent={() =>
                          analysisDone ? <CheckCircleIcon color="success" fontSize="small" /> :
                          analyzing ? <HourglassEmptyIcon color="warning" fontSize="small" /> :
                          <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                        }
                      >
                        Analyzed
                        <Chip label={analyzing ? 'Analyzing...' : analysisDone ? `Done (${analyses.length})` : analyses.length > 0 ? 'In Progress' : 'Not started'} size="small" sx={{ ml: 1 }}
                          color={analysisDone ? 'success' : (analyzing || analyses.length > 0) ? 'warning' : 'default'} />
                      </StepLabel>
                        <Box sx={{ pl: 4, pb: 1, pt: 0.5 }}>
                          {analyzing ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={14} />
                              <Typography variant="body2" color="text.secondary">Analyzing...</Typography>
                            </Box>
                          ) : (() => {
                            const analyzeContent = (
                              <Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0, mb: 0.5 }}>
                                  {prompts.map(p => (
                                    <FormControlLabel key={p.promptId}
                                      label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body2">{p.name}</Typography>
                                        {donePromptIds.has(p.promptId) && <Chip label="Done" size="small" color="success" sx={{ height: 18, fontSize: '0.6rem' }} />}
                                      </Box>}
                                      control={<Checkbox size="small" checked={selectedPromptIds.includes(p.promptId)}
                                        onChange={(e) => setSelectedPromptIds(prev => e.target.checked ? [...prev, p.promptId] : prev.filter(id => id !== p.promptId))} />}
                                    />
                                  ))}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <InputLabel>Model</InputLabel>
                                    <Select value={analysisModel} label="Model" onChange={(e) => setAnalysisModel(e.target.value)}>
                                      {MODEL_OPTIONS.map((m) => (
                                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <Button variant="outlined" size="small" onClick={handleAnalyze} disabled={!transcriptionDone || selectedPromptIds.length === 0}>
                                    Analyze with {selectedPromptIds.length} Prompt{selectedPromptIds.length !== 1 ? 's' : ''}
                                  </Button>
                                </Box>
                              </Box>
                            );
                            return analysisDone ? (
                              <>
                                <Typography variant="caption" color="primary" sx={{ cursor: 'pointer', mb: 0.5, display: 'block' }}
                                  onClick={() => setExpandAnalyze(!expandAnalyze)}>
                                  {expandAnalyze ? 'Hide options' : 'Show options'}
                                </Typography>
                                <Collapse in={expandAnalyze}>{analyzeContent}</Collapse>
                              </>
                            ) : analyzeContent;
                          })()}
                        </Box>
                    </Step>

                    {/* Extracted */}
                    <Step active completed={extractionComplete || ad?.extractionStatus === 'DONE'}>
                      <StepLabel
                        StepIconComponent={() =>
                          extractionComplete || ad?.extractionStatus === 'DONE' ? <CheckCircleIcon color="success" fontSize="small" /> :
                          extracting ? <HourglassEmptyIcon color="warning" fontSize="small" /> :
                          extractionFailed ? <CancelIcon color="error" fontSize="small" /> :
                          <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                        }
                      >
                        Extracted
                        {extractionFailed && !extracting && ad?.extractionError ? (
                          <Tooltip title={ad.extractionError} arrow>
                            <Chip label="Failed" size="small" sx={{ ml: 1, cursor: 'default' }} color="error" />
                          </Tooltip>
                        ) : (
                          <Chip label={extractionComplete || ad?.extractionStatus === 'DONE' ? 'Done' : extracting ? 'Processing' : extractionFailed ? 'Failed' : 'Not started'} size="small" sx={{ ml: 1 }}
                            color={extractionComplete || ad?.extractionStatus === 'DONE' ? 'success' : extracting ? 'warning' : extractionFailed ? 'error' : 'default'} />
                        )}
                      </StepLabel>
                        <Box sx={{ pl: 4, pb: 1, pt: 0.5 }}>
                          {extracting ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={14} />
                              <Typography variant="body2" color="text.secondary">Extracting...</Typography>
                            </Box>
                          ) : (() => {
                            const extractContent = (
                              <Box>
                                {extractionFailed && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="body2" color="error" sx={{ mb: 0.5 }}>Extraction failed.</Typography>
                                    {ad?.extractionError && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                                        {ad.extractionError}
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <InputLabel>Model</InputLabel>
                                    <Select value={extractionModel} label="Model" onChange={(e) => setExtractionModel(e.target.value)}>
                                      {MODEL_OPTIONS.map((m) => (
                                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <Button variant="outlined" size="small" onClick={handleExtract} disabled={!analysisDone || selectedAnalysisIds.length === 0}
                                    color={extractionFailed ? 'error' : undefined}>
                                    {extractionComplete ? 'Re-extract' : extractionFailed ? 'Retry' : 'Extract'}
                                  </Button>
                                </Box>
                              </Box>
                            );
                            return extractionComplete ? (
                              <>
                                <Typography variant="caption" color="primary" sx={{ cursor: 'pointer', mb: 0.5, display: 'block' }}
                                  onClick={() => setExpandExtract(!expandExtract)}>
                                  {expandExtract ? 'Hide options' : 'Show options'}
                                </Typography>
                                <Collapse in={expandExtract}>{extractContent}</Collapse>
                              </>
                            ) : extractContent;
                          })()}
                        </Box>
                    </Step>
                  </Stepper>
                </>}
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Typography>Ad not found</Typography>
        )}
      </DialogContent>
      <Snackbar
        open={!!error || !!toast}
        autoHideDuration={error ? 5000 : 2000}
        onClose={() => { clearError(); setToast(null); }}
        message={error || toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <Dialog open={!!confirmDeleteAnalysisId} onClose={() => setConfirmDeleteAnalysisId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete this analysis?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently removes the analysis row and its result. You can
            re-run the prompt afterward, but the cost (tokens already spent)
            won't come back.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteAnalysisId(null)} disabled={!!deletingAnalysisId}>
            Cancel
          </Button>
          <Button onClick={confirmDeleteAnalysis} color="error" variant="contained"
            disabled={!!deletingAnalysisId}>
            {deletingAnalysisId ? <><CircularProgress size={14} sx={{ mr: 1 }} /> Deleting…</> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
